import { AsyncReducer } from '@/types'
import { CartItem } from '@/types/data-types'
import { CartSummary } from '@/types/data-types/cart'
import { CartActions, CartState } from './types'
import { isNil } from '@/utils/validations'

export const getEmptyCart = (): CartState => {
  return {
    items: [],
    summary: {
      total: 0,
      amountDetails: [],
      quantity: 0
    }
  }
}

const calcCartTotal = async (items: CartItem[], summary?: CartSummary): Promise<CartState> => {
  if (items.length === 0) {
    return getEmptyCart()
  }

  let totalQty = 0
  let total = 0

  for (const item of items) {
    const { quantity, price = 0 } = item
    total += price * quantity
    totalQty += quantity
  }

  const subTotal = parseFloat((total / 1.11).toFixed(2))
  const vat = total - subTotal

  const amountDetails = [
    {
      amount: subTotal,
      key: 'subTotal'
    },
    {
      amount: vat,
      key: 'vat'
    }
  ]

  return {
    items,
    summary: {
      quantity: totalQty,
      total,
      amountDetails
    }
  }
}

const addItemToCart = async (state: CartState, item: CartItem): Promise<CartState> => {
  if (isNil(item.price)) {
    throw new Error('Invalid product. Price not found')
  }
  const existingCartItemIndex = state.items.findIndex(
    (i) => i.productId === item.productId
  )

  if (existingCartItemIndex > -1) {
    const newItems = [...state.items]
    // newItems[existingCartItemIndex].quantity = Math.min(newItems[existingCartItemIndex].quantity + item.quantity, MAX_QTY_PER_ITEM)
    newItems[existingCartItemIndex].quantity = newItems[existingCartItemIndex].quantity + item.quantity
    return await calcCartTotal(newItems, state.summary)
  }
  return await calcCartTotal([...state.items, item])
}

const removeItemFromCart = async (state: CartState, productId: number): Promise<CartState> => {
  const items = state.items.filter(item => item.productId !== productId)
  return await calcCartTotal(items, state.summary)
}

const updateCartItem = async (state: CartState, { productId, quantity }: { productId: number, quantity: number }): Promise<CartState> => {
  const existingCartItemIndex = state.items.findIndex(
    (i) => i.productId === productId
  )
  if (existingCartItemIndex > -1) {
    if (quantity === 0) {
      return await removeItemFromCart(state, productId)
    }
    const newItems = [...state.items]
    // newItems[existingCartItemIndex].quantity = Math.min(quantity, MAX_QTY_PER_ITEM)
    newItems[existingCartItemIndex].quantity = quantity
    return await calcCartTotal(newItems, state.summary)
  } else {
    throw new Error('Product not found in cart')
  }
}

export const reducer: AsyncReducer<CartState, CartActions> = async (state: any, action: any) => {
  switch (action.type) {
    case CartActions.FETCH_CART:
      return await calcCartTotal(state, action.payload)
    case CartActions.ADD_ITEM:
      return await addItemToCart(state, action.payload)
    case CartActions.REMOVE_ITEM:
      return await removeItemFromCart(state, action.payload)
    case CartActions.UPDATE_ITEM:
      return await updateCartItem(state, action.payload)
    case CartActions.CLEAR_CART:
      return getEmptyCart()
    default:
      throw new Error(`Invalid action: ${action.type as string}`)
  }
}
